import { useEffect, useState } from "react";
import { Layout } from "../components/layout.tsx";
import { useAuth } from "../context/auth.tsx";
import { type AiJob, listJobs } from "../api/jobs.ts";

const STATUS_COLORS: Record<string, string> = {
  QUEUED: "text-gray-400 bg-gray-800",
  RUNNING: "text-blue-400 bg-blue-900/30",
  COMPLETED: "text-green-400 bg-green-900/30",
  FAILED: "text-red-400 bg-red-900/30",
  RETRYING: "text-yellow-400 bg-yellow-900/30",
  CANCELLED: "text-gray-500 bg-gray-800",
};

export function JobsPage() {
  const { token } = useAuth();
  const [jobs, setJobs] = useState<AiJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (token == null) return;
    setLoading(true);
    listJobs(token, { limit: 50 })
      .then((res) => setJobs(res))
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "Failed to load jobs"))
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <Layout>
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-white">AI Jobs</h1>

        {loading && <p className="text-gray-400">Loading...</p>}
        {error != null && <p className="text-red-400">{error}</p>}
        {!loading && jobs.length === 0 && <p className="text-gray-500">No AI jobs yet.</p>}

        <div className="space-y-2">
          {jobs.map((job) => (
            <div
              key={job.id}
              className="space-y-2 rounded-lg border border-gray-800 bg-gray-900 p-4"
            >
              <div className="flex items-center justify-between gap-4">
                <span className="font-mono text-sm text-white">{job.agentName}</span>
                <span
                  className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[job.status] ?? "bg-gray-800 text-gray-400"}`}
                >
                  {job.status}
                </span>
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                {job.model != null && <span>model: {job.model}</span>}
                {job.tokensUsed != null && <span>tokens: {job.tokensUsed.toLocaleString()}</span>}
                {job.durationMs != null && <span>duration: {job.durationMs}ms</span>}
                <span>priority: {job.priority}</span>
                <span>
                  retries: {job.retryCount}/{job.maxRetries}
                </span>
                <span>created: {new Date(job.createdAt).toLocaleString()}</span>
              </div>
              {job.errorMessage != null && (
                <p className="font-mono text-xs text-red-400">{job.errorMessage}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
